import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { useAuthStore } from '@/store/authStore';
import { isProbablyMobile } from '@/utils/isProbablyMobile';
import { useSettings } from '@/hooks/useSettings';
import { toast } from 'sonner';

const loginSchema = z.object({
  email: z.string().email('Email non valida'),
  password: z.string().min(6, 'Password deve essere di almeno 6 caratteri'),
});

type LoginFormData = z.infer<typeof loginSchema>;

const LAST_EMAIL_KEY = 'studioGori_lastLoginEmail';

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [rememberEmail, setRememberEmail] = useState(() => {
    if (typeof window === 'undefined') return true;
    return !!localStorage.getItem(LAST_EMAIL_KEY);
  });
  const { login, isLoading } = useAuthStore();
  const { settings, settingsKey } = useSettings();
  const navigate = useNavigate();
  
  const companyName = settings?.general?.companyName || 'Studio Gori';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: typeof window !== 'undefined' ? localStorage.getItem(LAST_EMAIL_KEY) || '' : '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      await login(data);
      if (rememberEmail) {
        localStorage.setItem(LAST_EMAIL_KEY, data.email.trim());
      } else {
        localStorage.removeItem(LAST_EMAIL_KEY);
      }
      toast.success('Login effettuato con successo');
      const user = useAuthStore.getState().user;
      if (isProbablyMobile() && user?.role === 'OPERATOR' && user?.requiresAttendance !== false) {
        navigate('/timbra');
      } else {
        navigate('/dashboard');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Errore durante il login');
    }
  };

  return (
    <div key={settingsKey} className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">
            {companyName}
          </h1>
          <p className="mt-2 text-gray-600">
            Gestionale completo per {companyName}
          </p>
        </div>

        <Card className="w-full">
          <CardHeader>
            <CardTitle>Accedi al tuo account</CardTitle>
            <CardDescription>
              Inserisci le tue credenziali per accedere al gestionale
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="mario.rossi@example.com"
                  {...register('email')}
                  disabled={isLoading}
                />
                {errors.email && (
                  <p className="text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium">
                  Password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    {...register('password')}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="rememberEmail"
                  checked={rememberEmail}
                  onCheckedChange={(c) => setRememberEmail(c === true)}
                  disabled={isLoading}
                />
                <label htmlFor="rememberEmail" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Ricordami l&apos;email (solo su questo dispositivo)
                </label>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Accesso in corso...
                  </>
                ) : (
                  'Accedi'
                )}
              </Button>
            </form>

          </CardContent>
        </Card>
      </div>
    </div>
  );
}
